import React, { memo, useMemo } from 'react';
import { Box, Divider, Typography, Grid2 as Grid } from '@mui/material';
import { DiffItem, DiffTag, WordDiff } from './types';
import { DIFF_TAGS, DEFAULT_CONTEXT_LINES, DIFF_PREFIXES, DIFF_COLORS } from './constants';
import { buildHunks, getWordDiffsLCS } from './differenceHelper';

const HighlightedLine = ({
  words,
  prefix = '',
  tag,
  isLeft = false,
}: {
  words: WordDiff[];
  prefix?: string | boolean;
  tag: DiffTag;
  isLeft?: boolean;
}) => {
  const getWordStyle = (type: WordDiff['type']) => {
    switch (type) {
      case 'delete':
        return {
          backgroundColor: 'background.error',
          color: 'error.main',
          fontWeight: 'bold',
          padding: '0 2px',
          borderRadius: '2px',
        };
      case 'insert':
        return {
          backgroundColor: 'background.success',
          color: 'success.main',
          fontWeight: 'bold',
          padding: '0 2px',
          borderRadius: '2px',
        };
      case 'change':
        return {
          backgroundColor: 'background.warning',
          color: 'warning.main',
          fontWeight: 'bold',
          padding: '0 2px',
          borderRadius: '2px',
        };
      default: // 'equal'
        return {};
    }
  };

  /**
   * Get the base text color for the entire line
   * Different colors are used for different diff operations
   */
  const getLineColor = () => {
    if (isLeft) {
      return tag !== DIFF_TAGS.EQUAL ? DIFF_COLORS[DIFF_TAGS.DELETE] : DIFF_COLORS[DIFF_TAGS.EQUAL];
    }
    return tag === DIFF_TAGS.DELETE ? DIFF_COLORS[DIFF_TAGS.EQUAL] : DIFF_COLORS[tag];
  };

  return (
    <Typography
      component="div"
      sx={{
        color: getLineColor(),
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {prefix}
      {words.map((word, index) => (
        <Box
          component="span"
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          sx={getWordStyle(word.type)}
        >
          {word.text}
        </Box>
      ))}
    </Typography>
  );
};

const DiffLine = ({ left, right }: { left: React.ReactNode; right: React.ReactNode }) => (
  <Grid container spacing={1}>
    <Grid size={6}>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>{left}</Box>
    </Grid>
    <Grid size={6}>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>{right}</Box>
    </Grid>
  </Grid>
);

const DiffViewer = ({ difference }: { difference: DiffItem[] }) => {
  const hunks = useMemo(() => buildHunks(difference, DEFAULT_CONTEXT_LINES), [difference]);

  return (
    <Box>
      <Typography color="primary">Difference with the previous version</Typography>
      <Box
        component="pre"
        sx={{
          backgroundColor: 'background.default',
          color: 'text.primary',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          p: 2,
          borderRadius: 1,
          overflowX: 'auto',
          border: '1px solid',
          borderColor: 'border.default',
          mt: 0,
        }}
      >
        <DiffLine
          left={<Typography variant="subtitle1">Old Version</Typography>}
          right={<Typography variant="subtitle1">New Version</Typography>}
        />
        {hunks.map((hunk, hunkIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={`hunk-${hunkIndex}`} mb={2}>
            {hunkIndex !== 0 && <Divider sx={{ my: 1 }} />}
            {hunk.lines.map((line, lineIndex) => {
              const { leftWords, rightWords } = getWordDiffsLCS(line.oldLine, line.newLine);

              return (
                <DiffLine
                  // eslint-disable-next-line react/no-array-index-key
                  key={`line-${hunkIndex}-${lineIndex}`}
                  left={
                    <HighlightedLine
                      words={leftWords}
                      prefix={line.tag !== DIFF_TAGS.EQUAL ? DIFF_PREFIXES[DIFF_TAGS.DELETE] : ''}
                      tag={line.tag}
                      isLeft={true}
                    />
                  }
                  right={
                    <HighlightedLine
                      words={rightWords}
                      prefix={line.tag !== DIFF_TAGS.DELETE && DIFF_PREFIXES[line.tag]}
                      tag={line.tag}
                    />
                  }
                />
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default memo(DiffViewer);
